import * as React from 'react';
import { TextInput, Select } from "soprasteria-ui-library";
import { IReferral } from '../../../models/IReferral';
import { useAuth } from '../../../App';
import { ManageReferralApi } from '../../../api/ManageReferralApi';
import { ICareProvider } from '../../../models/ICareProvider';
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import { FORWARD_REFERRAL, FOLLOWUP_REFERRAL } from '../../../utils/ReferralTypeConstants';
import { IAddress } from '../../../models/IAddress';
import { CareProviderDropdown } from '../../CareProviderDropdown/CareProviderDropdown';

interface ICreateReferralFormInputs {
    referral?: IReferral;
}

export const CareProviderSection: React.FunctionComponent<ICreateReferralFormInputs> = ({ referral }) => {

    const auth = useAuth();
    const api = new ManageReferralApi(auth);
    const [careProviders, setCareProviders] = React.useState<ICareProvider[]>([]);
    const [selectedCareProvider, setSelectedCareProvider] = React.useState<ICareProvider>();
    const [careTypeFunctions, setCareTypeFunctions] = React.useState<IListItem[]>();
    const [isFetchingCareroviders, setIsFetchingCareroviders] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (referral?.isEditable) {
            api.getCareTypeFunctions((newCareTypeFunctions) => {
                const careTypeFunctionsListItems = newCareTypeFunctions.map<IListItem>((careTypeFunction) => { return { text: careTypeFunction.description, value: careTypeFunction.careTypeFunctionId.toString() } });
                setCareTypeFunctions(careTypeFunctionsListItems);
                if (referral.speciality && careProviders.length <= 0) {
                    getCareProviders(referral.speciality.specialityId.toString());
                }
            });
        }

        if (referral?.recipent) {
            const recipent = referral.recipent;
            const careProviderItem: ICareProvider = {
                name: recipent.name, careProviderId: recipent.careProviderId,
                address: { address: recipent.address?.address, zipCode: recipent.address?.zipCode, city: recipent.address?.city }
            };

            setSelectedCareProvider(careProviderItem)
            setCareProviders([...careProviders, careProviderItem]);
        }
        else {
            setCareProviders([]);
            setSelectedCareProvider(undefined)
        }

    }, [referral]);

    const formatAddress = (address?: IAddress): string => {
        if (address?.address === undefined || address?.zipCode === undefined || address?.city === undefined) {
            return "";
        }
        return `${address?.address?.trim()}, ${address?.zipCode} ${address?.city}`;
    }

    const getCareProviders = (careTypeFunctionId: string) => {
        setIsFetchingCareroviders(true);
        setCareProviders([]);

        api.getCareProvidersByCareType(careTypeFunctionId, (careProviders) => {
            setCareProviders(careProviders);
        }, undefined, () => {
            setIsFetchingCareroviders(false);
        });
    }

    const onCareProviderChange = (id: string) => {
        const newSelectedCareProvider = careProviders.find((careProvider) => careProvider.careProviderId.toString() == id);
        setSelectedCareProvider(newSelectedCareProvider);
    }

    return (
        <div className="row readonly-less-margin">
            <h5 className="col-12">Remissmottagare</h5>
            {
                (referral === undefined || (referral.isEditable && referral.referralTypeId !== FOLLOWUP_REFERRAL && referral.canEditRecipientCareProvider)) ?
                    <>
                        <div className="col-6">
                            <Select name="specialityId"
                                label="Specialitet"
                                options={careTypeFunctions ?? []}
                                selectedValue={referral?.speciality?.specialityId.toString()}
                                onChange={(careTypeFunctionId: string) => {
                                    setSelectedCareProvider(undefined);
                                    getCareProviders(careTypeFunctionId);
                                }}
                                placeholder="Välj en specialitet"
                                inlineLabel
                                required={(referral?.isEditable ?? false)}
                            />
                        </div>
                        <div className="col-6">
                            <CareProviderDropdown name="recipentCareProviderId"
                                careProviders={careProviders}
                                defaultValue={selectedCareProvider?.careProviderId.toString()}
                                isLoading={isFetchingCareroviders}
                                onValueChange={(id) => onCareProviderChange(id)}
                                required={referral?.isEditable ?? false}
                            />
                            {
                                selectedCareProvider ?
                                    <TextInput name="careProviderAddressReadonly" label="Mottagningens adress" defaultValue={formatAddress(selectedCareProvider.address)} inlineLabel readonly />
                                    : null
                            }
                        </div>
                    </>
                    :
                    <>
                        <div className="col-6">
                            <TextInput name="specialityReadonly" label="Specialitet" defaultValue={referral.speciality?.specialityName} readonly inlineLabel />
                        </div>
                        <div className="col-6">
                            <TextInput name="umbrellaReadonly" label="Vårdgivare" defaultValue={referral?.recipent?.displayOrganisationInformation} inlineLabel readonly />
                        </div>
                        <div className="col-6">
                            <TextInput name="recipentCareProviderReadonly" label="Mottagning" defaultValue={referral.recipent?.displayCareProviderName} readonly inlineLabel />
                        </div>
                        {
                            selectedCareProvider ?
                                <div className="col-6">
                                    <TextInput name="careProviderAddressReadonly" label="Mottagningens adress" defaultValue={formatAddress(referral.recipent?.address)} inlineLabel readonly />
                                </div>
                                : null
                        }
                        <div className="col-6">
                            <TextInput name="recipentPhoneNumberReadonly" label="Telefon" defaultValue={referral.recipent?.address?.phoneNumber} readonly inlineLabel />
                        </div>
                        <div className="col-6">
                            <TextInput name="recipentEmailReadonly" label="E-post" defaultValue={referral.recipent?.address?.emailAddress ?? "Saknas"} readonly inlineLabel />
                        </div>
                        {
                            referral.externalIdFromRecipient ?
                                <div className="col-6">
                                    <TextInput name="recipentexternalReferralNumberReadonly" label="Externt remiss-ID" defaultValue={referral.externalIdFromRecipient} readonly inlineLabel />
                                </div>
                                : null
                        }
                    </>
            }
        </div>
    )
}
